/**
 * Step 1: 产品信息输入
 */

import React from 'react';
import { Card, Form, Input, Button, Select, Tag, Divider } from 'antd';
import type { BrandBrief } from '../types';
import { useProductInfoStep } from './hooks/useProductInfoStep';
import { SellingPointsList } from './ProductInfoStepComponents/SellingPointsList';
import { SiteKeywordsCollapse } from './ProductInfoStepComponents/SiteKeywordsCollapse';

interface ProductInfoStepProps {
    productName: string;
    sellingPoints: string[];
    keywordsBySite: Record<string, string>;
    competitorTitles: string;
    selectedBrand?: BrandBrief;
    brands: BrandBrief[];
    onProductNameChange: (value: string) => void;
    onSellingPointsChange: (points: string[]) => void;
    onKeywordsBySiteChange: (keywords: Record<string, string>) => void;
    onCompetitorTitlesChange: (value: string) => void;
    onBrandChange: (brandId: number | undefined) => void;
    onNext: () => void;
    loading?: boolean;
}

export const ProductInfoStep: React.FC<ProductInfoStepProps> = ({
    productName,
    sellingPoints,
    keywordsBySite,
    competitorTitles,
    selectedBrand,
    brands,
    onProductNameChange,
    onSellingPointsChange,
    onKeywordsBySiteChange,
    onCompetitorTitlesChange,
    onBrandChange,
    onNext,
    loading,
}) => {
    const {
        addSellingPoint,
        updateSellingPoint,
        removeSellingPoint,
        updateSiteKeywords,
        canProceed,
    } = useProductInfoStep({
        sellingPoints,
        keywordsBySite,
        onSellingPointsChange,
        onKeywordsBySiteChange,
        productName,
    });

    return (
        <div className="product-info-step">
            <Card title="📦 产品基础信息" bordered={false}>
                <Form layout="vertical">
                    {/* 产品名称 */}
                    <Form.Item
                        label="产品名称"
                        required
                        tooltip="产品的完整名称，用于AI分析"
                    >
                        <Input
                            value={productName}
                            onChange={e => onProductNameChange(e.target.value)}
                            placeholder="例如：降噪蓝牙耳机 Pro 版"
                            size="large"
                        />
                    </Form.Item>

                    {/* 品牌选择 */}
                    <Form.Item
                        label="关联品牌"
                        tooltip="选择品牌后，文案将遵循品牌调性"
                    >
                        <Select
                            value={selectedBrand?.id}
                            onChange={onBrandChange}
                            placeholder="选择品牌（可选）"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            options={brands.map(b => ({
                                value: b.id,
                                label: b.name,
                            }))}
                        />
                        {selectedBrand && (
                            <div style={{ marginTop: 8 }}>
                                <Tag color="blue">调性: {selectedBrand.tone || '未设置'}</Tag>
                                <Tag color="green">原型: {selectedBrand.archetype || '未设置'}</Tag>
                            </div>
                        )}
                    </Form.Item>

                    <Divider />

                    {/* 核心卖点 */}
                    <SellingPointsList
                        sellingPoints={sellingPoints}
                        onUpdate={updateSellingPoint}
                        onAdd={addSellingPoint}
                        onRemove={removeSellingPoint}
                    />

                    {/* 竞品标题参考 */}
                    <Form.Item
                        label="竞品标题参考"
                        tooltip="输入竞品的标题（每行一个），AI将分析其关键词布局和结构优点，吸收长处并展现差异化"
                    >
                        <Input.TextArea
                            value={competitorTitles}
                            onChange={e => onCompetitorTitlesChange(e.target.value)}
                            placeholder="请粘贴优秀的竞品标题，每行一个..."
                            rows={4}
                            style={{ marginTop: 8 }}
                        />
                    </Form.Item>

                    <Divider />

                    {/* 分站点关键词 */}
                    <SiteKeywordsCollapse
                        keywordsBySite={keywordsBySite}
                        onUpdate={updateSiteKeywords}
                    />
                </Form>
            </Card>

            {/* 底部操作栏 */}
            <div style={{ marginTop: 24, textAlign: 'right' }}>
                <Button
                    type="primary"
                    size="large"
                    onClick={onNext}
                    loading={loading}
                    disabled={!canProceed}
                >
                    下一步：分析目标人群
                </Button>
            </div>
        </div>
    );
};

export default ProductInfoStep;
